import { Spinner } from "dash-ui";
import { useTranslation } from "react-i18next";
import {
  SubscriptionChangeMode,
  TrialUpgradeDetails,
} from "../SubscriptionChangeConfirmationModalV2.interfaces";
import {
  getConfirmButtonText,
  formatEndDate,
  MODAL_STYLES,
} from "../SubscriptionChangeConfirmationModalV2.helpers";
import { formatDateString } from "pages/ProfilePage/components/ProfileBilling/ProfileBilling.helpers";

interface ModalActionsProps {
  mode: SubscriptionChangeMode;
  totalDue: number;
  hasPaymentMethod: boolean;
  loading: boolean;
  loadingInvoice: boolean;
  currentPeriodEndAt?: Date | string;
  trial?: TrialUpgradeDetails;
  // DSH-837: extra gate for the confirm button (e.g. an unacknowledged
  // "leaving the API plan" warning) on top of the payment/loading checks.
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ModalActions({
  mode,
  totalDue,
  hasPaymentMethod,
  loading,
  loadingInvoice,
  currentPeriodEndAt,
  trial,
  confirmDisabled,
  onCancel,
  onConfirm,
}: ModalActionsProps) {
  const { t } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialUpgrade",
  });
  const { t: tc } = useTranslation(["pages", "common"]);
  const buttonText = trial
    ? t("confirmButton")
    : getConfirmButtonText(mode, totalDue, tc);
  const buttonColor = MODAL_STYLES.button[mode];

  return (
    <>
      <div className={`flex gap-3 ${trial ? "mt-4" : "mt-6"}`}>
        <button
          onClick={onCancel}
          className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm"
        >
          {tc("common:labels.cancel")}
        </button>
        <button
          onClick={onConfirm}
          disabled={
            !hasPaymentMethod || loadingInvoice || loading || confirmDisabled
          }
          className="flex-1 px-6 py-2.5 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          style={{ backgroundColor: buttonColor }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner size={16} />
              {tc("common:labels.processing")}
            </span>
          ) : (
            buttonText
          )}
        </button>
      </div>

      <p className="text-xs text-center text-gray-500 mt-3">
        {trial
          ? t("footerNote", { date: formatDateString(trial.endDate) })
          : mode === "upgrade"
          ? tc("pages:profilePage.billing.changeConfirm.footerCharged")
          : currentPeriodEndAt
          ? tc("pages:profilePage.billing.changeConfirm.footerDowngradeOn", {
              date: formatEndDate(currentPeriodEndAt),
            })
          : tc("pages:profilePage.billing.changeConfirm.footerDowngradeEnd")}
      </p>
    </>
  );
}
